import { useRef, useState, useEffect, useCallback } from 'react';
import { insightsShorts as store } from 'store';

export const useVideoControls = (handleNext, setGlobalPlayback) => {
  const videoRef = useRef(null);
  const progressRef = useRef(null);
  const [showedPoster, setShowedPoster] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showAnimatedButton, setShowAnimatedButton] = useState(false);
  const [videoPromise, setVideoPromise] = useState(null);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [progress, setProgress] = useState(0);
  const [volumeOfVideo, setVolumeOfVideo] = useState(100);
  const [dragging, setDragging] = useState(false);

  const setVideoPlayback = status => {
    if (setGlobalPlayback) {
      setGlobalPlayback(status);
    }

    setPlaying(status);
  };

  const playVideo = () => {
    if (!showControls) {
      setShowControls(true);
    }

    if (playing) return;

    if (videoPromise) {
      videoPromise.then(() => {
        videoRef.current?.play();
        setVideoPlayback(true);
      });
    } else {
      const playPromise = videoRef.current?.play();
      setVideoPromise(playPromise);
      playPromise.then(() => {
        videoRef.current?.play();
        setVideoPlayback(true);
      });
    }

    setShowedPoster(true);
  };

  const pauseVideo = () => {
    if (videoPromise && playing) {
      videoPromise.then(() => {
        videoRef.current?.pause();
        setVideoPlayback(false);
      });
    }
  };

  const togglePlayback = e => {
    if (e.target.localName === 'video') {
      setShowAnimatedButton(true);
    } else {
      setShowAnimatedButton(false);
    }
    if (playing) {
      pauseVideo();
      store.setPlaying(false);
    } else {
      playVideo();
      store.setPlaying(true);
    }
  };

  const resetVideo = () => {
    setShowedPoster(false);
    if (videoRef.current) {
      videoRef.current?.load();
    }
    setVideoPromise(null);
    setVideoPlayback(false);
    setVolumeOfVideo(100);
    if (muted) {
      setMuted(false);
    }
    if (showControls) {
      setShowControls(false);
    }
  };

  const seekTo = useCallback(
    e => {
      if (e.cancelable) {
        e.preventDefault();
      }
      e.stopPropagation();

      const progressBar = progressRef.current;
      const pageX = e?.touches?.[0].pageX ?? e.pageX;
      let offset = pageX - progressBar.getBoundingClientRect().left;
      const progressBarWidth = progressBar.offsetWidth;

      if (offset < 0) {
        offset = 0;
      } else if (offset > progressBarWidth) {
        offset = progressBarWidth;
      }

      const percentage = offset / progressBarWidth;
      const newTime = duration * percentage;

      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
      setProgress(percentage);
    },
    [currentTime, progress, duration, progressRef.current, videoRef.current],
  );

  const handleDown = e => {
    if (e.cancelable) {
      e.preventDefault();
    }
    e.stopPropagation();
    if (playing) {
      pauseVideo();
    }
    setDragging(true);
  };
  const handleMove = e => {
    if (dragging) {
      seekTo(e);
    }
  };

  const handleInteractionEnd = e => {
    if (e.cancelable) {
      e.preventDefault();
    }
    e.stopPropagation();
    if (!playing) {
      playVideo();
    }
    setDragging(false);
  };

  const onLoadedMetadata = e => {
    setDuration(e.target.duration);
  };

  const toggleMute = useCallback(() => {
    if (videoRef.current.muted) {
      videoRef.current.muted = false;
      setMuted(false);
      setVolumeOfVideo(videoRef.current.volume * 100);
      store.setMuted(false);
    } else {
      videoRef.current.muted = true;
      setMuted(true);
      setVolumeOfVideo(0);
      store.setMuted(true);
    }
  }, [muted]);

  const controlVolume = useCallback(
    e => {
      const volumeValue = parseFloat(e.target.value) / 100;

      if (volumeValue === 0) {
        setMuted(true);
        store.setMuted(true);
      } else {
        videoRef.current.muted = false;
        setMuted(false);
        store.setMuted(false);
      }

      setVolumeOfVideo(e.target.value);
      store.setVolumeOfVideo(e.target.value);

      videoRef.current.volume = volumeValue.toFixed(1);
    },
    [volumeOfVideo],
  );

  useEffect(() => {
    videoRef.current.addEventListener('canplay', () => setLoading(false));
    videoRef.current.addEventListener('waiting', () => setLoading(true));
  }, [videoRef.current]);

  useEffect(() => {
    const handleTimeUpdate = () => {
      if (videoRef.current) {
        const newCurrentTime = videoRef.current?.currentTime;
        const newProgress = newCurrentTime / duration;

        setCurrentTime(newCurrentTime);
        store.setCurrentTime(newCurrentTime);

        if (newProgress) {
          setProgress(newProgress);
          store.setProgress(newProgress);
        }
      }
    };

    videoRef.current?.addEventListener('timeupdate', handleTimeUpdate);

    return () => {
      videoRef.current?.removeEventListener('timeupdate', handleTimeUpdate);
    };
  }, [videoRef.current, duration]);

  useEffect(() => {
    if (typeof handleNext === 'function') {
      const playNext = () => {
        handleNext();
        videoRef.current.removeEventListener('ended', playNext);
      };

      videoRef.current.addEventListener('ended', playNext);
    }
  }, [handleNext, videoRef.current]);

  return {
    videoRef,
    progressRef,
    onLoadedMetadata,
    togglePlayback,
    playVideo,
    pauseVideo,
    resetVideo,
    playing,
    showedPoster,
    muted,
    setMuted,
    toggleMute,
    controlVolume,
    seekTo,
    loading,
    currentTime,
    progress,
    setProgress,
    duration,
    showControls,
    showAnimatedButton,
    volumeOfVideo,
    setVolumeOfVideo,
    handleDown,
    handleMove,
    handleInteractionEnd,
  };
};
